import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Box,
  Container,
  Avatar,
  IconButton,
  Chip,
  Fade,
  Alert,
} from '@mui/material';
import { 
  Group, 
  Extension, 
  Verified, 
  Autorenew, 
  Logout,
  Dashboard,
  TrendingUp,
  CloudDownload,
  AdminPanelSettings,
  Person,
  School,
  Subscriptions,
  Payment,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { auth, database } from '../firebase/Config';
import { ref, onValue } from 'firebase/database';
import { useAuth } from '../context/AuthContext';

const HomeComponent = () => {
  const [userEmail, setUserEmail] = useState('');
  const navigate = useNavigate();
  const { userRole } = useAuth();

  useEffect(() => {
    const user = auth.currentUser;
    if (user) {
      setUserEmail(user.email);
    }
  }, []);

  const handleLogout = async () => {
    await auth.signOut();
    navigate('/login');
  };

  // Definir todos os itens do menu com cores específicas e únicas
  const allMenuItems = [
    {
      title: 'Usuários',
      description: 'Gerencie usuários, crie novos e edite permissões',
      icon: <Group sx={{ fontSize: 48 }} />,
      path: '/users',
      color: '#48bb78',
      gradient: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)', // Verde
      adminOnly: true,
    },
    {
      title: 'Tipos de Plugins',
      description: 'Cadastre e gerencie os tipos de plugins disponíveis',
      icon: <Extension sx={{ fontSize: 48 }} />,
      path: '/plugins',
      color: '#ec4899',
      gradient: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)', // Rosa/Pink
      adminOnly: true,
    },
    {
      title: 'Licenças',
      description: 'Gerencie licenças, renovações e expirações',
      icon: <Verified sx={{ fontSize: 48 }} />,
      path: '/licenses',
      color: '#3b82f6',
      gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', // Azul
      adminOnly: true,
    },
    {
      title: 'Renovações',
      description: 'Cadastre renovações e acompanhe seu status',
      icon: <Autorenew sx={{ fontSize: 48 }} />,
      path: '/renewals',
      color: '#f59e0b',
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', // Laranja/Amber
      adminOnly: true,
    },
    {
      title: 'Downloads',
      description: 'Gerencie instaladores de plugins e versões',
      icon: <CloudDownload sx={{ fontSize: 48 }} />,
      path: '/downloads',
      color: '#10b981',
      gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', // Verde Esmeralda
      adminOnly: false,
    },
    {
      title: 'Cursos',
      description: 'Gerencie cursos e aulas do sistema EAD',
      icon: <School sx={{ fontSize: 48 }} />,
      path: '/admin/courses',
      color: '#8b5cf6',
      gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', // Roxo/Violeta
      adminOnly: true,
    },
    // {
    //   title: 'Planos de Assinatura',
    //   description: 'Gerencie planos e valores de assinatura',
    //   icon: <Subscriptions sx={{ fontSize: 48 }} />,
    //   path: '/plans',
    //   color: '#a855f7',
    //   gradient: 'linear-gradient(135deg, #a855f7 0%, #9333ea 100%)', // Roxo claro
    //   adminOnly: true,
    // },
    {
      title: 'Minhas Licenças',
      description: 'Visualize suas licenças e datas de expiração',
      icon: <Verified sx={{ fontSize: 48 }} />,
      path: '/my-licenses',
      color: '#06b6d4',
      gradient: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', // Ciano
      adminOnly: false,
      userOnly: true,
    },
    {
      title: 'Aprendizado & Suporte',
      description: 'Acesse cursos e tutoriais em vídeo sobre o sistema',
      icon: <School sx={{ fontSize: 48 }} />,
      path: '/learning',
      color: '#14b8a6',
      gradient: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)', // Teal/Verde-água
      adminOnly: false,
    },
    // {
    //   title: 'Assinatura',
    //   description: 'Assine um plano e renove sua licença automaticamente',
    //   icon: <Payment sx={{ fontSize: 48 }} />,
    //   path: '/subscription',
    //   color: '#f97316',
    //   gradient: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)', // Laranja forte
    //   adminOnly: false,
    // },
  ];

  // Filtrar itens baseado no role do usuário
  const menuItems = userRole === 'admin' 
    ? allMenuItems.filter(item => !item.userOnly) // Admin vê tudo exceto "Minhas Licenças"
    : allMenuItems.filter(item => !item.adminOnly); // User vê Downloads e Minhas Licenças

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
      {/* Header Profissional */}
      <AppBar 
        position="static" 
        elevation={0}
        sx={{ 
          background: 'linear-gradient(90deg, #48bb78 0%, #38a169 100%)',
          backdropFilter: 'blur(10px)',
        }}
      >
        <Toolbar sx={{ py: 1 }}>
          <Dashboard sx={{ mr: 2, fontSize: 32 }} />
          <Typography 
            variant="h5" 
            sx={{ 
              flexGrow: 1, 
              fontWeight: 700,
              letterSpacing: '0.5px',
            }}
          >
            Sistema de Gerenciamento de Licenças
          </Typography>
          {userEmail && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {/* Badge de Tipo de Usuário */}
              <Chip
                icon={userRole === 'admin' ? <AdminPanelSettings /> : <Person />}
                label={userRole === 'admin' ? 'Administrador' : 'Usuário'}
                size="small"
                sx={{
                  bgcolor: userRole === 'admin' ? '#48bb78' : 'rgba(255, 255, 255, 0.3)',
                  color: '#fff',
                  fontWeight: 600,
                  px: 1,
                  fontSize: '0.8rem',
                  '& .MuiChip-icon': {
                    color: '#fff',
                  },
                }}
              />
              <Chip 
                avatar={<Avatar sx={{ bgcolor: '#fff', color: '#48bb78' }}>{userEmail.charAt(0).toUpperCase()}</Avatar>}
                label={userEmail}
                sx={{ 
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  color: '#fff',
                  fontWeight: 500,
                  px: 1,
                }}
              />
              <IconButton 
                onClick={handleLogout}
                sx={{ 
                  color: '#fff',
                  '&:hover': { 
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.3s',
                }}
              >
                <Logout />
              </IconButton>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Welcome Section */}
      <Container maxWidth="lg" sx={{ mt: 6, mb: 4 }}>
        <Fade in timeout={800}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography 
              variant="h3" 
              sx={{ 
                fontWeight: 700,
                color: '#2d3748',
                mb: 2,
                textShadow: '2px 2px 4px rgba(0,0,0,0.1)',
              }}
            >
              Bem-vindo ao Painel de Controle
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                color: '#718096',
                fontWeight: 400,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
              }}
            >
              <TrendingUp /> Gerencie suas licenças de forma eficiente
            </Typography>
          </Box>
        </Fade>

        {/* Cards Grid */}
        <Grid container spacing={4} justifyContent="center">
          {menuItems.map((item, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Fade in timeout={1000 + (index * 200)}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 4,
                    overflow: 'hidden',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    background: '#fff',
                    '&:hover': {
                      transform: 'translateY(-12px)',
                      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
                      '& .card-header': {
                        transform: 'scale(1.05)',
                      },
                    },
                  }}
                >
                  {/* Card Header com Gradiente */}
                  <Box
                    className="card-header"
                    sx={{
                      background: item.gradient,
                      p: 4,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      transition: 'transform 0.3s',
                    }}
                  >
                    <Box sx={{ color: '#fff' }}>
                      {item.icon}
                    </Box>
                  </Box>

                  {/* Card Content */}
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Typography 
                      variant="h5" 
                      component="div"
                      sx={{ 
                        fontWeight: 700,
                        mb: 1.5,
                        color: '#2d3748',
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#718096',
                        lineHeight: 1.6,
                      }}
                    >
                      {item.description}
                    </Typography>
                  </CardContent>

                  {/* Card Action */}
                  <Box sx={{ p: 3, pt: 0 }}>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => navigate(item.path)}
                      sx={{
                        background: item.gradient,
                        color: '#fff',
                        fontWeight: 600,
                        py: 1.5,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontSize: '1rem',
                        boxShadow: 'none',
                        '&:hover': {
                          boxShadow: `0 8px 20px ${item.color}40`,
                          transform: 'scale(1.02)',
                        },
                        transition: 'all 0.3s',
                      }}
                    >
                      Acessar Módulo
                    </Button>
                  </Box>
                </Card>
              </Fade>
            </Grid>
          ))}
        </Grid>

        {/* Footer Info */}
        <Box sx={{ mt: 8, textAlign: 'center', pb: 4 }}>
          <Typography variant="body2" sx={{ color: '#718096' }}>
            © 2025 Sistema de Gerenciamento de Licenças | Todos os direitos reservados
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default HomeComponent;
